import {useQuery} from "@apollo/client";
import {useEffect, useState} from "react";
import {OrderType} from "../../../../graphql/types/OrderType";

import { GET_ORDER_LISTING_FOR_WMS, WMSOrderListResponse } from "../graphql/queries/getOrderListingForWMS";
import { WMSOrderFiltersType } from "../graphql/type/WMSOrderFiltersType";
import {useWarehouseContext} from "../../../../contexts/WarehouseContext";
import { buildParcelQueryFilters } from "../utils/buildParcelQueryFilters";

export interface ParcelQueryResult {
    parcelData: OrderType[];
    loading: boolean;
    refetch: (variables?: any) => Promise<any>;
    totalElements: number;
    accessiblePages: number;
    buildQueryVariables: () => any;
}

export const useWmsParcelQuery = (
    filters: WMSOrderFiltersType,
    currentPage: number,
    pageSize: number,
    sortField?: string | null,
    sortDirection?: 'ASC' | 'DESC' | null
): ParcelQueryResult => {
    const [parcelData, setParcelData] = useState<OrderType[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [accessiblePages, setAccessiblePages] = useState(0);

    // 获取仓库信息进行数据隔离
    const { currentWarehouse } = useWarehouseContext();

    // 查询参数构建统一收敛到 buildParcelQueryFilters（与「下载」入口共用同一份逻辑，避免漂移）
    const buildQueryVariables = () => {
        const queryFilters = buildParcelQueryFilters(filters, currentWarehouse);
        return {
            filters: queryFilters,
            pageRequest: {
                page: currentPage,
                size: pageSize,
                sortBy: sortField || "orderTime",
                sortDirection: sortDirection || "DESC"
            }
        };
    };

    const { data, loading: queryLoading, refetch } = useQuery<WMSOrderListResponse>(GET_ORDER_LISTING_FOR_WMS,
        {
            variables: buildQueryVariables(),
            fetchPolicy: 'no-cache'
        });

    useEffect(() => {
        // 当查询完成时（无论是否有数据），都应该更新加载状态
        if (!queryLoading) {
            setLoading(false);
        } else {
            setLoading(true);
        }

        const responseData = data?.getOrderListingForWMS?.content;
        if (responseData) {
            let sortedData = responseData;
            // 只有在没有用户主动排序时，才使用运单号顺序排序
            // 如果用户点击了列排序（sortField 不为空），则使用后端排序结果
            const trackingInput = filters.trackingNumbers;
            if (trackingInput && trackingInput.trim().length > 0 && !sortField) {
                // 解析输入的运单号列表：支持换行、逗号、空格分隔
                const inputTrackingList = trackingInput
                    .split(/[\r\n,\s]+/)
                    .map(t => t.trim())
                    .filter(t => t.length > 0);
                
                if (inputTrackingList.length > 0) {
                    // 构建输入顺序映射表：key为运单号（大写），value为在输入中的位置索引
                    const inputOrderMap = new Map<string, number>();
                    inputTrackingList.forEach((tracking, index) => {
                        inputOrderMap.set(tracking.toUpperCase(), index);
                    });
                    
                    // 按照输入顺序排序结果
                    sortedData = [...responseData].sort((a, b) => {
                        const aTracking = (a.trackingNumber || '').toUpperCase();
                        const bTracking = (b.trackingNumber || '').toUpperCase();
                        
                        // 获取每个运单号在输入中的位置索引，如果不存在则使用最大值排在最后
                        const aOrderIndex = inputOrderMap.has(aTracking) 
                            ? (inputOrderMap.get(aTracking) as number) 
                            : Number.MAX_SAFE_INTEGER;
                        const bOrderIndex = inputOrderMap.has(bTracking) 
                            ? (inputOrderMap.get(bTracking) as number) 
                            : Number.MAX_SAFE_INTEGER;
                        
                        // 按照索引排序
                        if (aOrderIndex !== bOrderIndex) {
                            return aOrderIndex - bOrderIndex;
                        }
                        return 0;
                    });
                }
            }
            setParcelData(sortedData);
        }
        const totalElements = data?.getOrderListingForWMS?.totalElements;
        if (totalElements !== undefined) {
            // GraphQL Long 可能是字符串，统一成 number，避免 R4 的 === 0 判断失败
            setTotal(Number(totalElements) || 0);
        }
        const respAccessiblePages = data?.getOrderListingForWMS?.accessiblePages;
        if (respAccessiblePages !== undefined) {
            setAccessiblePages(Number(respAccessiblePages) || 0);
        }
    }, [data, queryLoading, filters.trackingNumbers, sortField]);

    return {
        parcelData,
        loading: loading || queryLoading,
        refetch,
        totalElements: total,
        accessiblePages,
        buildQueryVariables
    };
};